import { useState, useEffect, FormEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from "react-simple-captcha";
import { useMutation } from "react-query";
import { CardMedia, TextField, Typography } from "@mui/material";
import { Replay } from "@mui/icons-material";
import { InputPassword } from "@/components/common";
import { ImageAssets } from "@/assets";
import { CommonUtils, StorageUtils } from "@/utils";
import { AuthService } from "src/services";
import { Navigate } from "react-router-dom";
import { KeyConstant, PathConstant } from "@/const";
import { useLoading } from "@/hooks";
import {
  RootBox,
  CaptchaBox,
  IconBtnReplay,
  BtnLogin,
  LeftBox,
  RightBox,
  LoginForm,
  LogoBox,
  CaptchaContainer,
} from "./Login.style";

const LoginPage = () => {
  const { t: getLabel } = useTranslation();
  const { openLoading } = useLoading();

  const [emailValue, setEmailValue] = useState<string>("");
  const [passwordValue, setPasswordValue] = useState<string>("");
  const [captchaCode, setCaptchaCode] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);

  const handleReloadCaptcha = () => {
    loadCaptchaEnginge(6);
  };

  const handleLoginSuccess = () => {
    openLoading(false);
  };

  const handleLoginFailed = () => {
    handleReloadCaptcha();
    setIsError(true);
    openLoading(false);
  };

  const mutationLogin = useMutation({
    mutationFn: () => AuthService.login(emailValue, passwordValue),
    onSuccess: handleLoginSuccess,
    onError: handleLoginFailed,
  });

  const handleLogin: FormEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    mutationLogin.mutate();
    openLoading(true);
  };

  useEffect(() => {
    setIsError(false);
  }, [emailValue, passwordValue, captchaCode]);

  useEffect(() => {
    handleReloadCaptcha();
  }, []);

  const isEnabledSubmit =
    CommonUtils.checkEmailFormat(emailValue) &&
    CommonUtils.checkPasswordFormat(passwordValue) &&
    validateCaptcha(captchaCode, false);

  return (
    <RootBox>
      <LeftBox>
        <LoginForm component="form" onSubmit={handleLogin}>
          <LogoBox>
            <CardMedia sx={{ marginBottom: "64px" }} component="img" image={ImageAssets.LogoImage} />
            <Typography variant="h4" align="center">
              {getLabel("L_SYSTEM_LOGIN")}
            </Typography>
          </LogoBox>

          <TextField
            autoComplete="email"
            autoFocus
            fullWidth
            label="Email"
            margin="normal"
            name="email"
            onChange={event => setEmailValue(event.target.value)}
            required
          />

          <InputPassword required margin="normal" onChange={event => setPasswordValue(event.target.value)} />

          <CaptchaBox>
            <TextField
              fullWidth
              autoComplete="off"
              label={getLabel("L_ENTER_VALIDATION")}
              onChange={event => setCaptchaCode(event.target.value)}
              required
            />
            <CaptchaContainer>
              <LoadCanvasTemplate reloadText=" " />
            </CaptchaContainer>
            <IconBtnReplay size="small" onClick={handleReloadCaptcha} edge="end" type="button">
              <Replay fontSize="small" />
            </IconBtnReplay>
          </CaptchaBox>

          {isError && <Typography sx={{ color: "red", marginTop: 2 }}>{getLabel("MSG_LOGIN_FAILED")}</Typography>}

          <BtnLogin fullWidth size="large" type="submit" variant="contained" disabled={!isEnabledSubmit}>
            {getLabel("L_LOGIN")}
          </BtnLogin>
        </LoginForm>

        {StorageUtils.getItem<string>(KeyConstant.KEY_TOKEN) && <Navigate to={PathConstant.MN_REQUEST} replace />}
      </LeftBox>
      <RightBox />
    </RootBox>
  );
};

export default LoginPage;
